import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { APP_FONT_STACK } from "../utils/common";

/**
 * JourneyAlertModal — the redesigned two-button alert dialogs
 * (Figma: Mobile / Modals Interaction), built on the shadcn Dialog primitive.
 *
 * One component, four presets. Copy is centralized here so wiring a state to a
 * modal is a one-liner:
 *
 *   <JourneyAlertModal
 *     variant="maxLimit"
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onPrimary={() => window.location.replace("https://www.iciciprulife.com/")}
 *   />
 *
 * Variants:
 *   recordingFailed  — Return to Home (single button, no secondary)
 *   maxLimit         — Close  / Try Again Later
 *   lastAttempt      — Close  / Continue Verification
 *   finalAttempt     — Close  / Contact Sales Team
 *
 * Each variant ships default primary/secondary labels; override any label or
 * handler via props. `onClose` is the default secondary/close handler and is
 * also fired by the × / Escape / outside-click (via Radix `onOpenChange`).
 *
 * A variant (or the `dismissible` prop) may set `dismissible: false` to make the
 * modal blocking: no × button, and Escape / outside-click are swallowed, so the
 * only way out is the primary button. Used for terminal errors like
 * recordingFailed where the user MUST return home.
 */
export const ALERT_VARIANTS = {
  recordingFailed: {
    title: "Video Consent Recording Failed",
    body: "Your video consent was not recorded successfully. Please restart the process and try again.",
    // No secondary action — this alert only offers "Return to Home".
    primary: "Return to Home",
    // Terminal error: only the primary button exits. No ×, Esc, or outside-click.
    dismissible: false,
  },
  maxLimit: {
    title: "Max Limit Reached",
    body: "You have reached the maximum limit for today. Please try again after 24 hours.",
    secondary: "Close",
    primary: "Try Again Later",
  },
  lastAttempt: {
    title: "Last Attempt Remaining",
    body: "You have only one attempt remaining to complete the verification process. Please ensure all information is accurate before proceeding.",
    secondary: "Close",
    primary: "Continue Verification",
  },
  finalAttempt: {
    title: "Final Verification Attempt",
    body: "You have only one last attempt remaining to complete the verification. Please ensure all information is accurate and follow the instructions carefully before proceeding.",
    secondary: "Close",
    primary: "Contact Sales Team",
  },
};

export default function JourneyAlertModal({
  variant,
  open,
  onClose,
  onPrimary,
  onSecondary,
  primaryText,
  secondaryText,
  bodyOverride,
  selectedLang,
  dismissible,
}) {
  const { t } = useTranslation();
  const preset = ALERT_VARIANTS[variant];
  if (!preset) return null;

  const fontFamily = APP_FONT_STACK;
  // Explicit prop wins; otherwise fall back to the variant, defaulting to true.
  const canDismiss = dismissible ?? preset.dismissible ?? true;

  const handleOpenChange = (next) => {
    if (!next && canDismiss && onClose) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        style={{ fontFamily }}
        showClose={canDismiss}
        {...(!canDismiss && {
          onInteractOutside: (e) => e.preventDefault(),
          onEscapeKeyDown: (e) => e.preventDefault(),
        })}
      >
        <DialogHeader>
          <DialogTitle>{t(preset.title)}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <DialogDescription className="mt-3">
            {t(bodyOverride || preset.body)}
          </DialogDescription>
        </DialogBody>

        <DialogFooter className="mt-6">
          {(secondaryText || preset.secondary) && (
            <button
              type="button"
              onClick={onSecondary || onClose}
              className="w-full rounded-lg bg-gray-100 px-5 py-2.5 text-base font-semibold text-black-150 hover:bg-gray-200 sm:w-auto"
            >
              {t(secondaryText || preset.secondary)}
            </button>
          )}
          <button
            type="button"
            onClick={onPrimary}
            className="w-full rounded-lg bg-orange-150 px-5 py-2.5 text-base font-semibold text-white hover:bg-orange-150/90 sm:w-auto"
          >
            {t(primaryText || preset.primary)}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
